import React from "react";
import { Link } from "react-router-dom";
import _ from "lodash";
import TableCell from "@material-ui/core/TableCell";
import placeholder from "lib/images/featured-image-tmp.png";
import { ACTIVE } from "constants/status";
import DealTableOptionsMenu from "components/Global/OptionsMenu/DealTable/DealTableOptionsMenu";
import numberFormatter from "utils/numberFormatter";
import moment from "moment";
import { map } from "constants/screenElements/closedDealTable";
import ClosedDealTableMetricFormatter from "components/Global/Metric/ClosedDealTableMetricFormatter";

const dealEditRedirect = id => `/portfolio/funds/free/deals/${id}`;

const getMetricValue = (deal, metricId, dotpath) => {
  const val = _.get(deal, dotpath, null);

  return ClosedDealTableMetricFormatter[metricId](val);
};

const getPrepaymentValue = (deal, metricId, dotpath) => {
  const val = _.get(deal, dotpath, null);
  const hasLockout = (deal.dealMetricsV2 && deal.dealMetricsV2.hasLockout);
  const data = { val, hasLockout };

  return ClosedDealTableMetricFormatter[metricId](data);
};

const ClosedDealTableColumns = () => [
  {
    id: "menu",
    isBaseColumn: true,
    dataField: null,
    layoutOrder: 99,
    numeric: false,
    Cell: ({ deal }) => (
      <TableCell className="loansList_Menu" key="menu">
        <DealTableOptionsMenu id={deal.id} dealEditRedirect={dealEditRedirect} />
      </TableCell>
    )
  },
  {
    id: "image",
    isBaseColumn: true,
    dataField: null,
    layoutOrder: -2,
    numeric: false,
    label: " ",
    Cell: ({ deal: { id, imageSmall }, linkPath }) => {
      let path = `/deals/${id}`;
      if (linkPath) {
        path = linkPath(id);
      }

      return (
        <TableCell className="loansList_TableImage">
          <Link to={path}>
            <img src={imageSmall || placeholder} alt="thumbnailImg" />
          </Link>
        </TableCell>
      );
    }
  },
  {
    id: "name",
    isBaseColumn: true,
    dataField: "name",
    layoutOrder: -1,
    numeric: false,
    label: "Deal",
    Cell: ({ deal: { name, id }, linkPath }) => {
      let path = `/deals/${id}`;
      if (linkPath) {
        path = linkPath(id);
      }

      return (
        <TableCell className="loansList_Link" id="closed_deal_table">
          <Link to={path}>
            {name}
          </Link>
        </TableCell>
      );
    }
  },
  {
    id: map.CURRENT_BALANCE,
    label: "Current Balance",
    dataField: "dealMetricsV2.currentBalance",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CURRENT_BALANCE, "dealMetricsV2.currentBalance");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.CASH_EQUITY,
    label: "Cash equity",
    dataField: "dealMetricsV2.cashEquity",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CASH_EQUITY, "dealMetricsV2.cashEquity");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.UNFUNDED_COMMITMENT,
    label: "Unfunded Commitment",
    dataField: "dealMetricsV2.unfundedCommitment",
    Cell: data => {
      const value = getMetricValue(data.deal, map.UNFUNDED_COMMITMENT, "dealMetricsV2.unfundedCommitment");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.CURRENT_RATE,
    label: "Current Rate",
    dataField: "dealMetricsV2.currentRate",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CURRENT_RATE, "dealMetricsV2.currentRate");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.PROJECTED_RATE,
    label: "Projected Rate",
    dataField: "dealMetricsV2.projectedRate",
    Cell: data => {
      const value = getMetricValue(data.deal, map.PROJECTED_RATE, "dealMetricsV2.projectedRate");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.UPCOMING_PAYMENT,
    label: "Upcoming Payment",
    dataField: "dealMetricsV2.upcomingPayment",
    Cell: data => {
      const value = getMetricValue(data.deal, map.UPCOMING_PAYMENT, "dealMetricsV2.upcomingPayment");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.MONTHS_UNTIL_MATURITY,
    label: "Months until maturity",
    dataField: "dealMetricsV2.monthsUntilMaturity",
    Cell: data => {
      const value = getMetricValue(data.deal, map.MONTHS_UNTIL_MATURITY, "dealMetricsV2.monthsUntilMaturity");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.IO_REMAINING,
    label: "I/O Remaining",
    dataField: "dealMetricsV2.ioRemaining",
    Cell: data => {
      const value = getMetricValue(data.deal, map.IO_REMAINING, "dealMetricsV2.ioRemaining");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.MAX_RECOURSE,
    label: "Max Recourse",
    dataField: "dealMetricsV2.maxRecourse",
    Cell: data => {
      const value = getMetricValue(data.deal, map.MAX_RECOURSE, "dealMetricsV2.maxRecourse");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.DSCR,
    label: "DSCR",
    dataField: "dealMetricsV2.dscrLender",
    Cell: data => {
      const value = getMetricValue(data.deal, map.DSCR, "dealMetricsV2.dscrLender");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.DSCR_ACTUAL,
    label: "DSCR Actual",
    dataField: "dealMetricsV2.dscrActual",
    Cell: data => {
      const value = getMetricValue(data.deal, map.DSCR_ACTUAL, "dealMetricsV2.dscrActual");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.DEBT_YIELD,
    label: "Debt Yield",
    dataField: "dealMetricsV2.debtYield",
    Cell: data => {
      const value = getMetricValue(data.deal, map.DEBT_YIELD, "dealMetricsV2.debtYield");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.LTV,
    label: "LTV",
    dataField: "dealMetricsV2.ltv",
    Cell: data => {
      const value = getMetricValue(data.deal, map.LTV, "dealMetricsV2.ltv");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.PREPAYMENT,
    label: "Prepayment",
    dataField: "dealMetricsV2.prepaymentPenalty",
    Cell: data => {
      const value = getPrepaymentValue(data.deal, map.PREPAYMENT, "dealMetricsV2.prepaymentPenalty");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.DEBT_RSF,
    label: "Debt/RSF",
    dataField: "dealMetricsV2.debtRsf",
    Cell: data => {
      const value = getMetricValue(data.deal, map.DEBT_RSF, "dealMetricsV2.debtRsf");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.DEBT_MF_UNIT,
    label: "Debt/MF Unit",
    dataField: "dealMetricsV2.debtMfUnit",
    Cell: data => {
      const value = getMetricValue(data.deal, map.DEBT_MF_UNIT, "dealMetricsV2.debtMfUnit");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.PROJECTED_EXIT_DATE,
    label: "Projected Exit Date",
    dataField: "dealMetricsV2.projectedExitDate",
    Cell: data => {
      const value = getMetricValue(data.deal, map.PROJECTED_EXIT_DATE, "dealMetricsV2.projectedExitDate");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.CAP_RATE,
    label: "Cap Rate",
    dataField: "dealMetricsV2.capRate",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CAP_RATE, "dealMetricsV2.capRate");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.PROJECT_TYPE,
    label: "Project Type",
    dataField: "deal.projectType.name",
    Cell: data => {
      const value = _.get(data, "deal.projectType.name", "--");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.CLOSING_DATE,
    label: "Closing Date",
    dataField: "dealMetricsV2.closingDate",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CLOSING_DATE, "dealMetricsV2.closingDate");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.PURCHASE_PRICE,
    label: "Purchase Price",
    dataField: "dealMetricsV2.purchasePrice",
    Cell: data => {
      const value = getMetricValue(data.deal, map.PURCHASE_PRICE, "dealMetricsV2.purchasePrice");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.MATURITY_DATE,
    label: "Maturity Date",
    dataField: "dealMetricsV2.maturityDate",
    Cell: data => {
      const value = getMetricValue(data.deal, map.MATURITY_DATE, "dealMetricsV2.maturityDate");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.EXTENDED_MATURITY_DATE,
    label: "Extended Maturity Date",
    dataField: "dealMetricsV2.extendedMaturityDate",
    Cell: data => {
      const value = getMetricValue(data.deal, map.EXTENDED_MATURITY_DATE, "dealMetricsV2.extendedMaturityDate");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.LENDER,
    label: "Lender",
    dataField: "dealMetricsV2.lenderName",
    Cell: data => {
      const value = getMetricValue(data.deal, map.LENDER, "dealMetricsV2.lenderName");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.HEDGE_TYPE,
    label: "Hedge Type",
    dataField: "dealMetricsV2.hedgeType",
    Cell: data => {
      const value = getMetricValue(data.deal, map.HEDGE_TYPE, "dealMetricsV2.hedgeType");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.HEDGE_MATURITY,
    label: "Hedge Maturity",
    dataField: "dealMetricsV2.hedgeMaturity",
    Cell: data => {
      const value = getMetricValue(data.deal, map.HEDGE_MATURITY, "dealMetricsV2.hedgeMaturity");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.HEDGE_MTM,
    label: "Hedge MtM",
    dataField: "dealMetricsV2.hedgeMarkToMarket",
    Cell: data => {
      const value = getMetricValue(data.deal, map.HEDGE_MTM, "dealMetricsV2.hedgeMarkToMarket");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.APPRAISED_VALUE,
    label: "Appraised Value",
    dataField: "dealMetricsV2.appraisedValue",
    Cell: data => {
      const value = getMetricValue(data.deal, map.APPRAISED_VALUE, "dealMetricsV2.appraisedValue");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.BROKER_COMPANY,
    label: "Broker Company",
    dataField: "deal.brokerCompany.name",
    Cell: data => {
      const brokerCompany = _.get(data, "deal.brokerCompany.name", "--");

      return (<TableCell>{brokerCompany}</TableCell>);
    }
  },
  {
    id: map.MSA,
    label: "MSA",
    dataField: "dealMetricsV2.msaName",
    Cell: data => {
      const value = getMetricValue(data.deal, map.MSA, "dealMetricsV2.msaName");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.CURRENT_BALANCE_FIXED,
    label: "Current Balance Fixed",
    dataField: "dealMetricsV2.currentBalanceFixed",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CURRENT_BALANCE_FIXED, "dealMetricsV2.currentBalanceFixed");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.CURRENT_BALANCE_FLOATING_ALL,
    label: "Current Balance Floating (All)",
    dataField: "dealMetricsV2.currentBalanceFloating",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CURRENT_BALANCE_FLOATING_ALL, "dealMetricsV2.currentBalanceFloating");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.CURRENT_BALANCE_HEDGED,
    label: "Current Balance Hedged",
    dataField: "dealMetricsV2.currentBalanceHedged",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CURRENT_BALANCE_HEDGED, "dealMetricsV2.currentBalanceHedged");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.EQUITY,
    label: "Current Balance Hedged",
    dataField: "dealMetricsV2.equity",
    Cell: data => {
      const value = getMetricValue(data.deal, map.EQUITY, "dealMetricsV2.equity");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.CURRENT_BALANCE_FLOATING_UNHEDGED,
    label: "Current Balance Floating (Unhedged)",
    dataField: "dealMetricsV2.currentBalanceFloatingUnhedged",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CURRENT_BALANCE_FLOATING_UNHEDGED, "dealMetricsV2.currentBalanceFloatingUnhedged");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.EXPENSE_RATIO,
    label: "Expense Ratio",
    dataField: "dealMetricsV2.expenseRatio",
    Cell: data => {
      const value = getMetricValue(data.deal, map.EXPENSE_RATIO, "dealMetricsV2.expenseRatio");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.REFI_PROCEEDS,
    label: "Refi Proceeds",
    dataField: "dealMetricsV2.loanProceeds",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CURRENT_BALANCE_HEDGED, "dealMetricsV2.loanProceeds");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.SUPPLEMENTAL_LOAN_PROCEEDS,
    label: "Supplemental Loan Proceeds",
    dataField: "dealMetricsV2.supplementalLoanProceeds",
    Cell: data => {
      const value = getMetricValue(data.deal, map.CURRENT_BALANCE_HEDGED, "dealMetricsV2.supplementalLoanProceeds");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.REFI_CASH_OUT,
    label: "Refi Cash Out",
    dataField: "dealMetricsV2.refiCashOut",
    Cell: data => {
      const value = getMetricValue(data.deal, map.REFI_CASH_OUT, "dealMetricsV2.refiCashOut");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.ALL_IN_EFFECTIVE_RATE,
    label: "All In Effective Rate",
    dataField: "dealMetricsV2.allInEffectiveRate",
    Cell: data => {
      const value = getMetricValue(data.deal, map.ALL_IN_EFFECTIVE_RATE, "dealMetricsV2.allInEffectiveRate");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.GOOD_NEWS_MONEY_REMAINING_DOLLAR_AMOUNT,
    label: "Good News Money Remaining ($)",
    dataField: "dealMetricsV2.goodNewsMoneyRemaining",
    Cell: data => {
      const value = getMetricValue(data.deal, map.GOOD_NEWS_MONEY_REMAINING_DOLLAR_AMOUNT, "dealMetricsV2.goodNewsMoneyRemaining");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.GOOD_NEWS_MONEY_REMAINING_PERCENT,
    label: "Good News Money Remaining (%)",
    dataField: "dealMetricsV2.goodNewsMoneyRemainingPercent",
    Cell: data => {
      const value = getMetricValue(data.deal, map.GOOD_NEWS_MONEY_REMAINING_PERCENT, "dealMetricsV2.goodNewsMoneyRemainingPercent");

      return (<TableCell>{value}</TableCell>);
    }
  },
  {
    id: map.EXTENSION_QUALIFICATION,
    label: "Extension Qualification",
    dataField: "dealMetricsV2.extensionQualification",
    Cell: data => {
      const value = getMetricValue(data.deal, map.EXTENSION_QUALIFICATION, "dealMetricsV2.extensionQualification");

      return (<TableCell>{value}</TableCell>);
    }
  }
];

export default ClosedDealTableColumns;

